import type { ReactNode } from "react";
import { Activity, Layers3, Radar } from "lucide-react";

import type { ModelSummaryResponse } from "../../types/api";
import { Badge } from "../ui/Badge";
import { Card } from "../ui/Card";

const warmupLabel: Record<string, string> = {
  idle: "Idle",
  loading: "Warming",
  ready: "Ready",
  error: "Error"
};

export function TopHeader({ summary }: { summary: ModelSummaryResponse | null }) {
  return (
    <Card className="overflow-hidden border-accent/20 bg-gradient-to-br from-[#181013] via-surface-panel to-[#0d0809]">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(320px,1fr)]">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Badge className="border-accent/30 bg-accent/10 text-[#ffd7df]">Single-page Research Demo</Badge>
            <Badge>{summary ? warmupLabel[summary.runtime.warmup_state] : "Loading Runtime"}</Badge>
          </div>
          <div className="space-y-3">
            <p className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">Horizon Vision</p>
            <p className="max-w-3xl text-base leading-7 text-ink-soft sm:text-lg">
              Upload an image, refine it in-browser, and run NH-LoRA inference in one cinematic workspace.
            </p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
          <HeaderStat icon={<Radar className="h-4 w-4" />} label="Runtime" value={summary ? summary.runtime.device.toUpperCase() : "CPU"} />
          <HeaderStat icon={<Layers3 className="h-4 w-4" />} label="Backbone" value={summary?.backbone_name ?? "Pending"} />
          <HeaderStat icon={<Activity className="h-4 w-4" />} label="Version" value={summary?.app_version ?? "0.1.0"} />
        </div>
      </div>
    </Card>
  );
}

function HeaderStat({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/5 px-4 py-4">
      <div className="mb-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink-muted">
        {icon}
        {label}
      </div>
      <p className="line-clamp-2 text-sm font-semibold text-white">{value}</p>
    </div>
  );
}
